import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMerchant } from "@auth";
import { fetchTaskMetrics } from "@api";
import { channels } from "../../../realtime/channels";
import { useCoalescedInvalidation } from "../../../realtime/useCoalescedInvalidation";
import { useActivityFeedEventNames } from "../../../realtime/useActivityFeedEventNames";

// Period keys match the BFF MetricsPeriod enum names. `label` is the selector text; `collectedLabel`
// is the phrasing used next to the collected figure.
export const METRIC_PERIODS = [
  { key: "Today", label: "Today", collectedLabel: "Collected today" },
  { key: "Last24Hours", label: "Last 24 hours", collectedLabel: "Collected in the last 24 hours" },
  { key: "ThisWeek", label: "This week", collectedLabel: "Collected this week" },
  { key: "Last7Days", label: "Last 7 days", collectedLabel: "Collected in the last 7 days" },
  { key: "ThisMonth", label: "This month", collectedLabel: "Collected this month" },
  { key: "Last30Days", label: "Last 30 days", collectedLabel: "Collected in the last 30 days" },
];

export const DEFAULT_METRIC_PERIOD = "Today";

const readStoredPeriod = (storageKey) => {
  try {
    const stored = localStorage.getItem(storageKey);
    return METRIC_PERIODS.some((p) => p.key === stored) ? stored : DEFAULT_METRIC_PERIOD;
  } catch {
    return DEFAULT_METRIC_PERIOD;
  }
};

// A metric-period selection persisted to localStorage under `storageKey` so it survives page swaps.
// Each caller passes its own key, so the header figure and the rail panel stay independent.
export const useMetricPeriod = (storageKey) => {
  const [period, setPeriodState] = useState(() => readStoredPeriod(storageKey));

  const setPeriod = (key) => {
    setPeriodState(key);
    try {
      localStorage.setItem(storageKey, key);
    } catch {
      // Ignore storage failures (private mode / quota) — the choice still applies for this session.
    }
  };

  return [period, setPeriod];
};

// Task-manager dashboard metrics for a period. collected/requestsSent come from payment-service;
// documentsSent/actionsCompleted/aiAnswers are best-effort (0 when their service is unavailable).
//
// Kept live off the same realtime hint the Activity feed uses: any merchant activity (a payment
// landing, a request or document sent, an AI action) refetches the figures, so the collected total
// and tiles move in step with the feed rather than going stale until a manual refresh. A burst of
// hints is folded into one refetch (useCoalescedInvalidation). Invalidating the whole
// ["taskMetrics"] key refreshes every mounted period at once — the header figure and the panel,
// whatever each has selected.
export const useTaskMetrics = (period = DEFAULT_METRIC_PERIOD) => {
  const { merchant } = useMerchant();

  const query = useQuery({
    queryKey: ["taskMetrics", period],
    queryFn: async ({ signal }) => {
      const data = await fetchTaskMetrics(period, { signal });
      return {
        collected: data?.collected ?? 0,
        requestsSent: data?.requestsSent ?? 0,
        documentsSent: data?.documentsSent ?? null,
        actionsCompleted: data?.actionsCompleted ?? null,
        aiAnswers: data?.aiAnswers ?? null,
      };
    },
    staleTime: 60_000,
    retry: false,
  });

  const channelPath = merchant?.id ? channels.merchantActivity(merchant.id) : null;
  // The figures move on the same activities the feed shows.
  const eventNames = useActivityFeedEventNames("general");
  useCoalescedInvalidation(channelPath, ["taskMetrics"], { eventNames });

  return query;
};
